// ═══════════════════════════════════════════════════════════════════════════════
// Admin — search users, membership filter, open account detail
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AdminUserListItem, AdminUserListResponse } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { adminPortalPath } from '../../lib/adminPortalPaths';

const levelShortLabel = (level: number | null): string => {
  if (level == null) {
    return '—';
  }
  if (level === 3) {
    return 'L3';
  }
  if (level === 2) {
    return 'L2';
  }
  if (level === 1) {
    return 'L1';
  }
  return '—';
};

export const AdminUsersPage = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'all' | 'employer' | 'helper'>('all');
  const [paired, setPaired] = useState<'all' | 'true' | 'false'>('all');
  const [level, setLevel] = useState<'all' | '1' | '2' | '3'>('all');
  const [accountRemovalRequested, setAccountRemovalRequested] = useState<'all' | 'true' | 'false'>(
    'all',
  );
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminUserListResponse | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const q = new URLSearchParams({
        page: String(page),
        pageSize: '20',
        role,
        isPaired: paired,
        level,
        accountRemovalRequested,
      });
      if (email.trim()) {
        q.set('email', email.trim());
      }
      const res = await adminApi.get<AdminUserListResponse>(`/admin/users?${q.toString()}`);
      if (res.success && res.data) {
        setData(res.data);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [email, page, role, paired, level, accountRemovalRequested, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.users')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.usersSubtitle')}</p>

      <div className="mt-6 flex flex-wrap gap-3 rounded-lg border border-slate-800 bg-slate-900/60 p-4">
        <input
          type="search"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          placeholder={t('admin.usersEmailFilter')}
          className="min-w-[200px] flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.usersEmailFilter')}
        />
        <select
          value={role}
          onChange={(ev) => {
            setPage(1);
            setRole(ev.target.value as 'employer' | 'helper' | 'all');
          }}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.usersRoleFilter')}
        >
          <option value="all">{t('admin.filter.roleAll')}</option>
          <option value="employer">{t('admin.filter.roleEmployer')}</option>
          <option value="helper">{t('admin.filter.roleHelper')}</option>
        </select>
        <select
          value={paired}
          onChange={(ev) => {
            setPage(1);
            setPaired(ev.target.value as 'all' | 'true' | 'false');
          }}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.usersPairedFilter')}
        >
          <option value="all">{t('admin.filter.pairedAll')}</option>
          <option value="true">{t('admin.filter.pairedYes')}</option>
          <option value="false">{t('admin.filter.pairedNo')}</option>
        </select>
        <select
          value={level}
          onChange={(ev) => {
            setPage(1);
            setLevel(ev.target.value as 'all' | '1' | '2' | '3');
          }}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.usersLevelFilter')}
        >
          <option value="all">{t('admin.filter.levelAll')}</option>
          <option value="1">{t('admin.filter.level1')}</option>
          <option value="2">{t('admin.filter.level2')}</option>
          <option value="3">{t('admin.filter.level3')}</option>
        </select>
        <select
          value={accountRemovalRequested}
          onChange={(ev) => {
            setPage(1);
            setAccountRemovalRequested(ev.target.value as 'all' | 'true' | 'false');
          }}
          className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          aria-label={t('admin.usersRemovalRequestedFilter')}
        >
          <option value="all">{t('admin.filter.removalRequestedAll')}</option>
          <option value="true">{t('admin.filter.removalRequestedYes')}</option>
          <option value="false">{t('admin.filter.removalRequestedNo')}</option>
        </select>
        <button
          type="button"
          className="rounded-md bg-slate-800 px-4 py-2 text-sm text-white hover:bg-slate-700"
          onClick={() => void load()}
        >
          {t('task.action.refresh')}
        </button>
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <div className="mt-6 overflow-x-auto rounded-lg border border-slate-800">
        <table className="min-w-full divide-y divide-slate-800 text-left text-sm">
          <thead className="bg-slate-900/80 text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">{t('admin.email')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.usersColNickname')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.usersColRole')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.usersColLevel')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.usersColPaired')}</th>
              <th className="px-3 py-2 font-medium">{t('admin.usersColActions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {(data?.users ?? []).map((u: AdminUserListItem) => (
              <tr key={u.id} className="hover:bg-slate-900/40">
                <td className="px-3 py-2 text-slate-200">{u.email}</td>
                <td className="px-3 py-2 text-slate-300">{u.nickname}</td>
                <td className="px-3 py-2 text-slate-300">{u.role}</td>
                <td className="px-3 py-2 text-slate-300">{levelShortLabel(u.level)}</td>
                <td className="px-3 py-2 text-slate-300">{u.isPaired ? t('admin.yes') : t('admin.no')}</td>
                <td className="px-3 py-2">
                  <Link
                    to={adminPortalPath(`users/${u.id}`)}
                    className="text-amber-400 hover:underline"
                    tabIndex={0}
                    aria-label={t('admin.usersViewDetail')}
                  >
                    {t('admin.usersViewDetail')}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data ? (
        <p className="mt-4 text-xs text-slate-500">
          {t('admin.usersPageInfo', {
            page: data.page,
            total: data.totalCount,
          })}
        </p>
      ) : null}

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          disabled={page <= 1}
          className="rounded-md border border-slate-700 px-3 py-1 text-sm disabled:opacity-40"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('admin.prevPage')}
        </button>
        <button
          type="button"
          disabled={data ? page * data.pageSize >= data.totalCount : true}
          className="rounded-md border border-slate-700 px-3 py-1 text-sm disabled:opacity-40"
          onClick={() => setPage((p) => p + 1)}
        >
          {t('admin.nextPage')}
        </button>
      </div>
    </div>
  );
};
